<template>
  <v-dialog
    v-model="show"
    persistent
    min-width="500"
    max-width="90%"
    class="ma-0 pa-0"
  >
    <v-card class="rounded-lg pa-0 ma-0">
      <v-card-title color="primary" class="primary primary-title">
        <v-icon color="white darken-3" large>online_prediction</v-icon>
        <span class="white--text title pl-2"> Run Machine Learning Model</span>
      </v-card-title>
      <v-card-text class="pa-2 ma-0">
        <v-stepper v-model="step" class="pa-0 ma-0">
          <v-stepper-header>
            <v-stepper-step :complete="step > 1" step="1" class="px-3">
              Select model
            </v-stepper-step>
            <v-stepper-step :complete="step > 2" step="2" class="px-3">
              Input/Review patient data
            </v-stepper-step>
            <v-stepper-step
              :complete="'probability' in modelOutput"
              step="3"
              class="px-3"
            >
              Run classifier and view results
            </v-stepper-step>
          </v-stepper-header>
          <v-divider></v-divider>

          <v-stepper-items>
            <v-stepper-content step="1">
              <div class="h3 pa-2 mb-3" style="background-color: #e0e0e0;">
                Selected Model:
                {{ selectedModels.length > 0 ? selectedModels[0].name : '' }}
              </div>
              <v-divider></v-divider>

              <v-data-table
                v-model="selectedModels"
                show-select
                :single-select="true"
                :items="models"
                item-key="name"
                :headers="modelHeaders"
                class="pt-4"
              >
                <template v-slot:item.name="{ item }">
                  <td class="subtitle-1 text-xs-left">{{ item.name }}</td>
                </template>

                <template v-slot:item.reference="{ item }">
                  <td class="subtitle-1 text-xs-left" style="width: 40%;">
                    {{ item.reference }}
                  </td>
                </template>

                <template v-slot:item.inputs="{ item }">
                  <td class="subtitle-1 text-xs-left">
                    {{ item.inputs.join(', ') }}

                    <!-- 1 column layout -->
                    <ul v-if="false">
                      <li
                        v-for="(i, inum) in item.inputs"
                        :key="'oc-in-' + inum"
                      >
                        {{ i }}
                      </li>
                    </ul>
                  </td>
                </template>

                <template v-slot:item.output="{ item }">
                  <td
                    class="subtitle-1 text-xs-left"
                    style="vertical-align: top; width: 300px;"
                  >
                    {{ item.outputDescription }}
                    <!-- 2 column layout -->
                    <v-container fluid fill-width>
                      <v-row class="pa-0 ma-0">
                        <!-- 1st column -->
                        <v-col cols="6" class="pa-0 ma-0">
                          <v-container fluid fill-width class="ma-0 pa-0">
                            <v-row
                              v-for="(i, inum) in oddsOrEvens(
                                item.output,
                                'evens'
                              )"
                              :key="'oc-c1-' + inum"
                              align="center"
                              class="pa-0 ma-0"
                            >
                              <v-col cols="12" class="pa-0 ma-0">
                                {{ i }}<br clear="both" />
                                <audiogram-region-icon
                                  :width="120"
                                  :profile="i"
                                  class="pr-3"
                                >
                                </audiogram-region-icon>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-col>

                        <!-- 2nd column -->
                        <v-col cols="6" class="ma-0 pa-0 pl-3">
                          <v-container fluid fill-width class="ma-0 pa-0">
                            <v-row
                              v-for="(i, inum) in oddsOrEvens(
                                item.output,
                                'odds'
                              )"
                              :key="'oc-c2-' + inum"
                              align="center"
                              class="pa-0 ma-0"
                            >
                              <v-col cols="12" class="pa-0 ma-0">
                                {{ i }}<br clear="both" />
                                <audiogram-region-icon
                                  :width="120"
                                  :profile="i"
                                >
                                </audiogram-region-icon>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-col>
                      </v-row>
                    </v-container>
                  </td>
                </template>
              </v-data-table>
              <v-divider class="my-2"></v-divider>
              <v-container fluid fill-width class="ma-0 pa-0">
                <v-row>
                  <v-col cols="12" align="right">
                    <v-btn text color="red lighten-2" @click="close()">
                      <v-icon left>close</v-icon> Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      :disabled="selectedModels.length == 0"
                      @click="step = 2"
                      >Select Model</v-btn
                    >
                  </v-col>
                </v-row>
              </v-container>
            </v-stepper-content>

            <!-- Enter inputs and run -->
            <v-stepper-content step="2">
              <div class="h3 pa-2 mb-3" style="background-color: #e0e0e0;">
                Selected Model:
                {{ selectedModels.length > 0 ? selectedModels[0].name : '' }}
              </div>
              <v-divider></v-divider>

              <v-form v-if="selectedModels.length > 0">
                <v-container fluid fill-width class="ma-0 pa-0 pt-4">
                  <v-row
                    v-for="(inp, inpi) in selectedModels[0].inputs"
                    :key="'vr-inp-' + inpi"
                    align="center"
                  >
                    <v-col
                      cols="4"
                      align="right"
                      style="vertical-align: center;"
                    >
                      {{ inp }}:
                    </v-col>
                    <v-col cols="8">
                      <v-text-field
                        v-model="modelInputs[inp]"
                        outlined
                        dense
                        hide-details
                      >
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>

              <v-divider class="my-2"></v-divider>
              <v-container fluid fill-width class="ma-0 pa-0">
                <v-row>
                  <v-col cols="12" align="right">
                    <v-btn text color="red lighten-2" @click="close()">
                      <v-icon left>close</v-icon> Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      :disabled="selectedModels.length == 0 || modelRunning"
                      @click="runModel()"
                      >Run Model</v-btn
                    >
                  </v-col>
                </v-row>
              </v-container>
            </v-stepper-content>

            <!-- View results -->
            <v-stepper-content step="3">
              <div class="h3 pa-2 mb-3" style="background-color: #e0e0e0;">
                Selected Model:
                {{ selectedModels.length > 0 ? selectedModels[0].name : '' }}
              </div>
              <v-divider></v-divider>

              <!-- running model -->
              <div v-if="modelRunning">
                Model running.
                <loading-spinner></loading-spinner>
              </div>

              <!-- error running model -->
              <div v-else-if="modelError != null" class="h3 pa-2 mb-3">
                <v-icon color="error" large>mdi-alert</v-icon>
                FAILED! {{ modelError }}
              </div>

              <!-- successful run -->
              <div v-else-if="'probability' in modelOutput">
                <v-container fluid fill-width class="ma-0 pa-0 subtitle-1">
                  <v-row>
                    <v-col cols="2"> Probability/score: </v-col>
                    <v-col cols="10"> {{ modelOutput['probability'] }} </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="2"> Threshold/cutoff: </v-col>
                    <v-col cols="10"> {{ modelOutput['threshold'] }} </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="2"> Predicted class: </v-col>
                    <v-col cols="10">
                      <v-container
                        fluid
                        fill-width
                        class="ma-0 pa-0 subtitle-1"
                      >
                        <v-row>
                          <div
                            v-for="(i, inum) in selectedModels[0].output"
                            :key="'soc-' + inum"
                            class="pa-4 my-3 justify-center align-center"
                            :style="getDivStyle(i)"
                          >
                            {{ i }}<br clear="both" />
                            <audiogram-region-icon
                              :width="120"
                              :profile="i"
                              class="pr-3"
                            >
                            </audiogram-region-icon>
                            <br clear="both" />
                            <v-icon
                              v-if="modelOutput['predicted_class'] == i"
                              x-large
                              color="success"
                              >mdi-check-bold</v-icon
                            >
                          </div>
                        </v-row>
                      </v-container>
                    </v-col>
                  </v-row>
                </v-container>
              </div>

              <v-divider class="my-2"></v-divider>
              <v-container fluid fill-width class="ma-0 pa-0">
                <v-row>
                  <v-col cols="12" align="right">
                    <v-btn text color="primary" @click="close()">
                      <v-icon left dark>close</v-icon> Close
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

import AudiogramRegionIcon from '@/components/PatientAudiogram/AudiogramRegionIcon.vue';
import { mapState } from 'vuex';
import { state } from '@/store/modules/dataExplorer/types';

var MODELS = [
  {
    id: 1,
    name: 'Mi and Sun Random Forest NHANES HL Classifier',
    reference:
      'Mi Y, Sun P. Machine learning-based prediction of hearing loss: Findings of the US NHANES from 2003 to 2018. Hear Res. 2025 Jun;461:109252. doi: 10.1016/j.heares.2025.109252. Epub 2025 Mar 30. PMID: 40187231.',
    inputs: [
      'Age',
      'Blood lead (ug/dL)',
      'Thallium, urine (ug/L)',
      'BMI',
      'Antimony, urine (ug/L)',
      'Energy (kcal)',
      'Calcium (mg)',
      'Vitamin E (mg)',
      'Cobalt, urine (ug/L)',
      'Arsenic, Total - Urine (ug/L)',
      'Molybdenum, urine (ug/L)',
      'Cesium, urine (ug/L)',
      'Vitamin A, RAE (mcg)',
      'Barium, urine (ug/L)',
      'Magnesium (mg)',
      'Hypertension',
      'Sex',
      'Job loud noise',
      'Non-job noise exposure',
      'Diabetes',
      'Race',
    ],
    output: ['HL - yes', 'HL - no'],
    description: '',
    outputDescription: 'Binary yes/no hearing loss classification:',
    trainSetSize: 2814,
  },
  // not implemented
  /**
  {
    id: 2,
    name: 'Ellis and Souza Random Forest NHANES WARHICS Classifier',
    reference:
      'Ellis GM, Souza PE. Using Machine Learning and the National Health and Nutrition Examination Survey to Classify Individuals With Hearing Loss. Front Digit Health. 2021 Aug 18;3:723533. doi: 10.3389/fdgth.2021.723533. PMID: 34713189; PMCID: PMC8521948.',
    inputs: [
      'Age',
      'Gender',
      '2-4 kHz slope',
      'Military experience',
      'Self-reported hearing ability',
    ],
    output: [
      'Level 1',
      'Level 2',
      'Level 3',
      'Level 4',
      'Level 5',
      'Level 6',
      'Level 7',
      'Level 8',
    ],
    description: '',
    outputDescription:
      'Wisconsin Age-Related Hearing Impairment Classification Scale (WARHICS):',
    trainSetSize: 9256,
  },
**/
];

export default {
  components: {
    AudiogramRegionIcon,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    selectedPatient: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      step: 1,
      models: MODELS,
      selectedModels: [],
      modelHeaders: [
        { text: 'Model', value: 'name', class: 'subtitle-1' },
        { text: 'Reference', value: 'reference', class: 'subtitle-1' },
        {
          text: 'Dataset',
          value: 'trainSetSize',
          class: 'subtitle-1',
          sortable: false,
        },
        { text: 'Inputs', value: 'inputs', class: 'subtitle-1' },
        { text: 'Output Classes', value: 'output', class: 'subtitle-1' },
      ],
      modelInputs: {},
      obsVarMapping: {},
      modelRunning: false,
      modelOutput: {},
      modelError: null,
    };
  },
  computed: {
    ...mapState('dataExplorer', {
      collection: state.COLLECTION,
    }),
  },
  watch: {
    show(sv) {
      if (sv) {
        this.step = 1;
        this.selectedModels = [];
        this.modelInputs = {};
        this.obsVarMapping = {};
        this.modelRunning = false;
        this.modelOutput = {};
        this.modelError = null;
      }
    },
    step(snum) {
      // model selected
      if (snum == 2) {
        // populate model inputs/patient info from database
        this.initModelInputs();
        this.updateObsVarMapping();
        if (this.selectedPatient != null) this.populateModelInputsFromPatient();
      }
      // run model
      else if (snum == 3) {
        // TODO
      }
    },
  },
  methods: {
    close() {
      this.$emit('dialogClosed');
    },
    oddsOrEvens(a, which) {
      var res = [];
      for (var i = 0; i < a.length; ++i) {
        let m2 = i % 2;
        if (m2 == 0 && which == 'evens') {
          res.push(a[i]);
        } else if (m2 == 1 && which == 'odds') {
          res.push(a[i]);
        }
      }
      return res;
    },
    initModelInputs() {
      this.selectedModels[0]['inputs'].forEach(inp => {
        this.modelInputs[inp] = '';
      });
    },
    populateModelInputsFromPatient() {
      this.selectedModels[0]['inputs'].forEach(inp => {
        if (inp in this.selectedPatient) {
          this.modelInputs[inp] = this.selectedPatient[inp];
        } else if (inp in this.obsVarMapping) {
          let inp_id = this.obsVarMapping[inp]['id'];
          // TODO - cross-sectional only
          this.modelInputs[inp] = this.selectedPatient[inp_id].value;
        } else {
          //          console.log('unable to find input variable ' + inp);
        }
      });
    },
    updateObsVarMapping() {
      var ov_map = {};
      this.collection.observation_variables_list.forEach(ov => {
        ov_map[ov.ontology.abbreviation] = ov.ontology;
      });
      this.obsVarMapping = ov_map;
    },
    async runModel() {
      var request_data = {};
      Object.keys(this.modelInputs).forEach(k => {
        var v = this.modelInputs[k];
        request_data[k] = v;
      });
      var model_url = '/api/models/' + this.selectedModels[0].id;
      this.modelRunning = true;
      axios
        .post(model_url, request_data)
        .then(({ data }) => {
          this.modelOutput = data;
          this.modelRunning = false;
          this.modelError = data.error;
        })
        .catch(err => {
          this.modelError = err;
          this.modelRunning = false;
        });
      this.step = 3;
    },
    getDivStyle(cl) {
      if (cl == this.modelOutput['predicted_class']) {
        return 'border: 4px solid green;';
      } else {
        return '';
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
